import React, { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import {
  Button,
  CustomDropdown,
  Modal,
  TrashIcon,
  fieldLabelClasses,
} from "../../../components/ui";
import { HOUR_OPTIONS, MINUTE_OPTIONS } from "../data/durationOptions";
import {
  createExerciseEntry,
  durationParts,
  totalMinutes,
} from "../exerciseMapping";
import { useExerciseTypes } from "../hooks/useExerciseTypes";
import type {
  ExerciseEntry,
  ExerciseTypeOption,
} from "../types/exercisePlans.types";

const HOURS = HOUR_OPTIONS.map((hours) => ({
  value: String(hours),
  label: `${hours}hr`,
}));

const MINUTES = MINUTE_OPTIONS.map((minutes) => ({
  value: String(minutes),
  label: `${minutes}m`,
}));

interface ExerciseRowProps {
  entry: ExerciseEntry;
  /** Which line this is, for the labels a screen reader reads off the fields. */
  index: number;
  /** `GET /api/v1/exercise-types/`, fetched once for the whole dialog. */
  types: ExerciseTypeOption[];
  /** What the field says while the catalog is on its way, or did not come. */
  typePlaceholder: string;
  /**
   * What is wrong with the line, said under it — set by the dialog once Send
   * has been pressed on a line that cannot go. Both duration boxes redden
   * with it: the duration is one answer asked in two boxes.
   */
  error?: string;
  onChange: (entry: ExerciseEntry) => void;
  /**
   * Unset on the last line standing. A dialog with no rows in it has nothing
   * to fill in and no way back to having one but the add button, so the bin
   * goes rather than emptying the form — the row is cleared by clearing its
   * fields.
   */
  onRemove?: () => void;
}

const ExerciseRow: React.FC<ExerciseRowProps> = ({
  entry,
  index,
  types,
  typePlaceholder,
  error,
  onChange,
  onRemove,
}) => {
  const { hours, minutes } = durationParts(entry.minutes);
  const position = index + 1;
  const errorId = `exercise-${entry.id}-error`;

  // Keyed by name rather than by id: the field's value is what it displays,
  // and the id is carried alongside so a create can send it later. A name the
  // catalog does not hold is one that was typed — which the backend stores the
  // same way, as an exercise with no `exercise_type_id`.
  const options = useMemo(
    () => types.map((type) => ({ value: type.name, label: type.name })),
    [types],
  );

  const setDuration = (parts: { hours?: number; minutes?: number }) =>
    onChange({
      ...entry,
      minutes: totalMinutes({ hours, minutes, ...parts }),
    });

  return (
    <div>
      {/* `items-start`, so a row whose type field has its panel open does not
          drag the duration boxes beside it out of line. */}
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <CustomDropdown
            options={options}
            value={entry.type}
            onChange={(type) =>
              onChange({
                ...entry,
                type,
                // The id only survives while the name is one the catalog gave.
                // A typed name is an exercise the backend has no row for, and
                // keeping the last id beside it would file it under the wrong
                // one.
                typeId: types.find((row) => row.name === type)?.id ?? null,
              })
            }
            placeholder={typePlaceholder}
            searchable
            // The catalog is a list of suggestions rather than a closed set: a
            // stored plan can carry an exercise with `exercise_type_id: null`
            // and a name of its own, so the dialog offers the same.
            creatable
            heading="Exercise type"
            aria-label={`Exercise type ${position}`}
          />
        </div>

        <div className="w-[100px] 3xl:w-[120px] shrink-0">
          <CustomDropdown
            options={HOURS}
            value={String(hours)}
            onChange={(value) => setDuration({ hours: Number(value) })}
            heading="Hours"
            aria-label={`Hours for exercise ${position}`}
            invalid={!!error}
            aria-describedby={error ? errorId : undefined}
          />
        </div>

        <div className="w-[100px] 3xl:w-[120px] shrink-0">
          <CustomDropdown
            options={MINUTES}
            value={String(minutes)}
            onChange={(value) => setDuration({ minutes: Number(value) })}
            heading="Minutes"
            aria-label={`Minutes for exercise ${position}`}
            invalid={!!error}
            aria-describedby={error ? errorId : undefined}
          />
        </div>

        {/* Kept in the layout when there is nothing to press, so the boxes above
          and below it stay in one column — see `onRemove`. */}
        <div className="w-6 shrink-0 pt-2.5">
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove exercise ${position}`}
              className="rounded text-[#E5484D] transition-colors hover:text-[#C62A2F] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Under the whole row rather than under one box: it is the pair that
          is wrong, and a line squeezed under a 100px box would wrap into
          three. The same red line `FieldControls` draws. */}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-1.5 text-[12px] text-red-500"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export interface ExerciseFormModalProps {
  onClose: () => void;
  /**
   * The lines to open on, where the dialog is being come back to — from the
   * send step, cancelled — rather than opened fresh. Left off, it opens on
   * one blank row.
   */
  initialEntries?: ExerciseEntry[];
  /**
   * Given the finished lines — what happens to them is the caller's to do,
   * and may be a request, so this may be awaited.
   *
   * The dialog stays open and usable until it resolves, then the caller closes
   * it. A write that was refused therefore leaves every answer where it was,
   * to be tried again without being typed a second time.
   */
  onSubmit: (entries: ExerciseEntry[]) => void | Promise<void>;
}

/**
 * Create exercise: the lines a new plan is written from.
 *
 * A plan is written once and read after that — there is no way back into this
 * dialog on a plan that exists. The one way it opens on anything is
 * `initialEntries`: the send step that follows this dialog can be cancelled,
 * and that comes back here with what was typed rather than to a blank row.
 *
 * Mounted only while it is open, so every answer is forgotten on close instead
 * of being left over for the next plan.
 */
const ExerciseFormModal: React.FC<ExerciseFormModalProps> = ({
  onClose,
  initialEntries,
  onSubmit,
}) => {
  // Fetched with the dialog rather than with the panel behind it: the catalog
  // is only picked from here, and a tab that never opens this never asks for
  // it. Cached for the session, so the second opening is instant.
  const { options: types, status: typesStatus } = useExerciseTypes();

  const typePlaceholder =
    typesStatus === "loading"
      ? "Loading exercises…"
      : typesStatus === "failed"
        ? "Type an exercise"
        : "Cycling";

  // One blank row to fill in. The plus under it is what says there can be
  // more; a second row opened empty read as a question the plan had to
  // answer. Never fewer than one — the trash is off the last row.
  const [rows, setRows] = useState<ExerciseEntry[]>(() =>
    initialEntries?.length ? initialEntries : [createExerciseEntry()],
  );

  // Handing the lines on may be a request, and a second one would write a
  // second plan.
  const [saving, setSaving] = useState(false);

  // Whether Send has been pressed. What is wrong with a line is only said
  // from then on: a row opens on 0hr 0m, and reddening it before anyone has
  // had the chance to pick a duration would be telling them off for opening
  // the dialog. Once said, it clears itself as the row is fixed — the check
  // below is over the rows as they stand, not a list written at the time.
  const [attempted, setAttempted] = useState(false);

  // A line is only worth storing once it says what is being done. A row
  // opened and left blank is not an exercise with no name.
  const filled = useMemo(() => rows.filter((row) => row.type.trim()), [rows]);

  /**
   * The lines that say what is done but not for how long. 0hr 0m is what a
   * row opens on, not an answer — an exercise the client is told to do for
   * no time at all reads as a mistake, because it is one — so a plan does not
   * go until every named line has a duration.
   */
  const untimed = useMemo(
    () => new Set(filled.filter((row) => row.minutes === 0).map((r) => r.id)),
    [filled],
  );

  const rowError = (row: ExerciseEntry) =>
    attempted && untimed.has(row.id)
      ? `Set how long ${row.type.trim()} runs — 0hr 0m is not a duration.`
      : undefined;

  const update = (entry: ExerciseEntry) =>
    setRows((current) =>
      current.map((row) => (row.id === entry.id ? entry : row)),
    );

  const remove = (id: string) =>
    setRows((current) => current.filter((row) => row.id !== id));

  const handleSubmit = async () => {
    if (!filled.length || saving) return;

    // Said under the rows in question, and the dialog stays put. The button
    // is not disabled for this the way it is for an empty form: a button
    // that would not press, with nothing saying why, leaves the dietitian
    // hunting for the box they missed.
    setAttempted(true);
    if (untimed.size > 0) return;

    setSaving(true);
    try {
      // Only the lines that were filled in. A row left blank is one the
      // dietitian opened and did not use, not an exercise with no name.
      await onSubmit(filled);
    } finally {
      // The dialog is unmounted by the caller on success, so this only ever
      // runs after a failure — which is exactly when it has to be usable
      // again.
      setSaving(false);
    }
  };

  /**
   * What the button says.
   *
   * "Send exercise" rather than "Save", because writing a plan is what puts it
   * in the client's hands — it goes to them as it is written, and there is no
   * draft state for an exercise plan to sit in the way a meal plan has one.
   * It leads to the send dialog, where the addresses are; the write happens
   * from there, together with the send.
   */
  const submitLabel = saving ? "Sending…" : "Send exercise";

  return (
    <Modal
      open
      title="Create exercise"
      onClose={onClose}
      className="max-w-[680px]"
      scrollBody
      footer={
        <>
          <Button
            variant="secondary"
            size="md"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size="md"
            disabled={filled.length === 0 || saving}
            onClick={handleSubmit}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* One pair of headings over the columns rather than a label per box:
            every row asks the same two questions, and repeating them four
            times down the dialog would read as four different questions. */}
        <div className="flex items-center gap-3">
          <span className={`${fieldLabelClasses} min-w-0 flex-1`}>
            Exercise type
          </span>
          <span
            className={`${fieldLabelClasses} w-[212px] 3xl:w-[252px] shrink-0`}
          >
            Duration
          </span>
          <span className="w-6 shrink-0" aria-hidden="true" />
        </div>

        {rows.map((row, index) => (
          <ExerciseRow
            key={row.id}
            entry={row}
            index={index}
            types={types}
            typePlaceholder={typePlaceholder}
            error={rowError(row)}
            onChange={update}
            onRemove={rows.length > 1 ? () => remove(row.id) : undefined}
          />
        ))}

        <div>
          <button
            type="button"
            onClick={() =>
              setRows((current) => [...current, createExerciseEntry()])
            }
            aria-label="Add another exercise"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-brand text-brand transition-colors hover:bg-[#E6FAFB] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ExerciseFormModal;
